import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CALCULATORS = [
  {
    id: 'sip',
    name: 'SIP Calculator',
    tagline: 'Estimate your SIP growth over time.',
    to: '/calculators/sip',
  },
  {
    id: 'swp',
    name: 'SWP Calculator',
    tagline: 'Plan steady income from mutual funds.',
    to: '/calculators/swp',
  },
  {
    id: 'retirement',
    name: 'Retirement Calculator',
    tagline: 'See if you are on track for retirement.',
    to: '/calculators/retirement',
  },
  {
    id: 'fire',
    name: 'FIRE Calculator',
    tagline: 'Calculate your financial independence number.',
    to: '/calculators/fire',
  },
  {
    id: 'goal',
    name: 'Goal Planner',
    tagline: 'Plan SIPs for multiple financial goals.',
    to: '/calculators/goal',
  },
  {
    id: 'emi',
    name: 'EMI Calculator',
    tagline: 'Compute EMI for loans and compare options.',
    to: '/calculators/emi',
  },
];

export default function FinancialCalculatorsCarousel({
  title = 'Financial Calculators',
  subtitle = 'Plan smarter with free tools built specifically for investors',
  backgroundColor = '#1F2937',
}) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const itemsPerPage =
    typeof window !== 'undefined' && window.innerWidth >= 1024 ? 6 :
    typeof window !== 'undefined' && window.innerWidth >= 768 ? 2 : 1;

  const maxIndex = Math.max(0, CALCULATORS.length - itemsPerPage);
  const visibleItems = useMemo(
    () => CALCULATORS.slice(currentIndex, currentIndex + itemsPerPage),
    [currentIndex, itemsPerPage]
  );

  const showNavigation = CALCULATORS.length > itemsPerPage;

  const handlePrev = () => setCurrentIndex((prev) => Math.max(0, prev - 1));
  const handleNext = () => setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));

  return (
    <section className="py-20" style={{ backgroundColor }} data-testid="financial-calculators-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-text-primary mb-4">
            {title}
          </h2>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="relative">
          {/* Navigation Arrows */}
          {showNavigation && (
            <>
              <button
                onClick={handlePrev}
                disabled={currentIndex === 0}
                className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-3 rounded-full glass-effect transition-all ${
                  currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-primary/20 hover:scale-110'
                }`}
                aria-label="Previous calculators"
                data-testid="calc-prev"
              >
                <ChevronLeft className="h-6 w-6 text-text-primary" />
              </button>

              <button
                onClick={handleNext}
                disabled={currentIndex >= maxIndex}
                className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-3 rounded-full glass-effect transition-all ${
                  currentIndex >= maxIndex ? 'opacity-30 cursor-not-allowed' : 'hover:bg-primary/20 hover:scale-110'
                }`}
                aria-label="Next calculators"
                data-testid="calc-next"
              >
                <ChevronRight className="h-6 w-6 text-text-primary" />
              </button>
            </>
          )}

          {/* Cards Grid */}
          <div className="overflow-hidden px-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {visibleItems.map((calc, index) => (
                  <Link key={calc.id} to={calc.to} data-testid={`calc-card-link-${index}`}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className="glass-effect p-8 rounded-2xl relative h-full transition-all hover:border-secondary/30 hover:-translate-y-1"
                      data-testid={`calc-card-${calc.id}`}
                    >
                      <div className="mb-4">
                        <Calculator className="h-12 w-12 text-primary" />
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">
                        {calc.name}
                      </h3>
                      <p className="text-text-secondary">
                        {calc.tagline}
                      </p>
                    </motion.div>
                  </Link>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Pagination Dots */}
          {showNavigation && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-primary w-6' : 'bg-white/20 hover:bg-white/40'
                  }`}
                  aria-label={`Go to calculator page ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
